import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useCheckoutV1, useQuery } from '../../../helpers/customHooks';
import { clearPaymentV1, redoV1 } from '../../../store/checkoutV1/actions';
import { ICheckoutStoreState } from '../../../store/checkoutV1/types';
import { AnalyticService } from '../../../services';
import { useRoutesHelper } from '../../../helpers/routesHelper';
import { useDispatch } from '../../../store';
import { setResultInfo } from '../../../store/resultInformarion/actions';
import { AlertType, KeyErrors, KeySuccess, StripePaymentStatus } from '../../../store/enums';
import { Routes } from '../../../store/routes';
import { CheckoutHelper } from '../../../helpers';
import { ResultInfoButtonConfigType } from '../../../store/resultInformarion/types';
import { clearBook } from '../../../store/bookV1/actions';
import { GlobalStateType } from '../../../configureStore';

const StripeBookResultPageV1 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const analyticSrv = new AnalyticService();
  const query = useQuery();

  const paymentIntentId = query.get('payment_intent');
  const redirectStatus = query.get('redirect_status');

  const { checkoutComplete, checkoutCancel } = useCheckoutV1();

  const { getResultStatusRoute, getCheckoutRoute, getUserTicketsRouteV1 } = useRoutesHelper();

  const checkout = useSelector<GlobalStateType, ICheckoutStoreState>((state) => state.checkoutV1);
  const isAuthorized = useSelector<GlobalStateType, boolean>((state) => state.app.isAuthorized);

  const isCompleted = checkout?.complete?.result?.isCompleted;
  const isSuccess = checkout?.complete?.result?.isSuccess;
  const isProcessing = checkout?.complete?.result?.isProcessing;

  const handlePaymentFiled = () => {
    checkoutCancel(checkout).then(() => {
      dispatch(
        setResultInfo({
          type: AlertType.Error,
          key: KeyErrors.PaymentFailed,
          title: 'Result.PaymentErrorHasOccurred',
          message: 'Result.PleaseTryAgainLater',
          buttons: [
            {
              title: 'Result.GoToMainPage',
              callback: () => navigate(Routes.Main, { replace: true }),
            },
          ],
        })
      );
      navigate(getResultStatusRoute(AlertType.Error, KeyErrors.PaymentFailed), { replace: true });
    });
  };

  const handlePaymentCompletion = (checkout: ICheckoutStoreState) => {
    const completeResult = checkout.complete.result;
    let alertType: AlertType;
    let alertKey: KeySuccess | KeyErrors;
    let title = completeResult.userMessage;
    let message = completeResult.userMessageDetails;
    let callbacks: Array<ResultInfoButtonConfigType> = [];

    if (completeResult.isProcessing) {
      alertType = AlertType.Success;
      alertKey = KeySuccess.PaymentInProcess;
      title = 'Alert.WaitForTheServerToCompleteThePayment';
      callbacks = [
        {
          title: 'Result.GoToMainPage',
          callback: () => navigate(Routes.Main, { replace: true }),
        },
      ];
      navigate(getResultStatusRoute(alertType, alertKey), { replace: true });
    } else if (!completeResult.isCompleted) {
      alertType = AlertType.Error;
      alertKey = KeyErrors.PaymentFailed;
      callbacks = [
        {
          title: 'Result.TryAgain',
          callback: () => {
            dispatch(redoV1());
            navigate(getCheckoutRoute(checkout.eventSlug), { replace: true });
          },
        },
        {
          title: 'Result.Cancel',
          callback: () => {
            checkoutCancel(checkout).then(() => {
              navigate(Routes.Main, { replace: true });
            });
          },
        },
      ];
      navigate(getResultStatusRoute(alertType, alertKey), { replace: true });
    } else {
      alertType = completeResult.isSuccess ? AlertType.Success : AlertType.Error;
      alertKey = completeResult.isSuccess ? KeySuccess.PaymentSucceeded : KeyErrors.PaymentFailed;
      message = isAuthorized && completeResult.isSuccess ? 'Result.TicketsAvailabilityMessage' : message;
      callbacks = [
        {
          title: 'Result.GoToMainPage',
          callback: () => navigate(Routes.Main, { replace: true }),
        },
      ];

      if (completeResult.isSuccess && isAuthorized) {
        callbacks.push({
          title: 'Result.MyTickets',
          callback: () => navigate(getUserTicketsRouteV1(), { replace: true }),
        });
      }

      if (completeResult.isSuccess) {
        analyticSrv.trackEvent('Purchase', {
          value: checkout.init.priceTotal,
          currency: checkout.init.currency,
        });
      }
      if (CheckoutHelper.getIsProcessed(checkout) && !CheckoutHelper.getIsCompleted(checkout)) {
        (async () => await checkoutCancel(checkout))();
      }
      navigate(getResultStatusRoute(alertType, alertKey), { replace: true });
    }

    dispatch(
      setResultInfo({
        type: alertType,
        key: alertKey,
        title,
        message,
        buttons: callbacks,
      })
    );
  };

  useEffect(() => {
    const cancel = redirectStatus === StripePaymentStatus.canceled;
    if (cancel) {
      checkoutCancel(checkout).then(() => {
        dispatch(
          setResultInfo({
            type: AlertType.Error,
            key: KeyErrors.PaymentFailed,
            title: 'Result.PaymentErrorHasOccurred',
            message: 'Result.SorryYourPurchaseHasBeenCancelled',
            buttons: [
              {
                title: 'Result.GoToMainPage',
                callback: () => {
                  navigate(Routes.Main, { replace: true });
                },
              },
            ],
          })
        );
        navigate(getResultStatusRoute(AlertType.Error, KeyErrors.PaymentFailed), { replace: true });
      });
    }
  }, [redirectStatus]);

  useEffect(() => {
    if (!checkout.complete) {
      return;
    }

    const succeeded = redirectStatus === StripePaymentStatus.succeeded;
    if (CheckoutHelper.getIsCompleted(checkout) && succeeded) {
      handlePaymentCompletion(checkout);

      dispatch(clearPaymentV1());
      dispatch(clearBook());
    } else {
      handlePaymentFiled();
    }
  }, [isCompleted, isSuccess, isProcessing, redirectStatus]);

  useEffect(() => {
    (async () => await checkoutComplete(paymentIntentId))();
  }, [paymentIntentId]);

  return null;
};

export default StripeBookResultPageV1;
